import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Menu,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetOutsourcedRequests,
  apiUpdateOutsourcingStatus,
} from "../../api/outsourcing";
import { formatDate } from "../../utils/url-id";
import { useNotificationService } from "../../components/notification";
import { LoadingButton } from "@mui/lab";

const OutsourcedJobs = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationService();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusData, setStatusData] = useState({
    status: "pending",
    notes: "",
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["outsourced-requests", page, statusFilter],
    queryFn: () =>
      apiGetOutsourcedRequests({
        page: page.toString(),
        filter: statusFilter ? `filter[status]=${statusFilter}` : "",
      }),
  });

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: () =>
      apiUpdateOutsourcingStatus({
        requestId: selectedRequest._id,
        status: statusData.status,
        notes: statusData.notes,
      }),
    onSuccess: (data) => {
      if (data?.status === "error") {
        showNotification(data?.msg || "Failed to update status", "error");
        return;
      }
      showNotification("Status updated successfully", "success");
      setUpdateDialogOpen(false);
      setSelectedRequest(null);
      setStatusData({ status: "pending", notes: "" });
      queryClient.invalidateQueries(["outsourced-requests"] as any);
    },
    onError: () => {
      showNotification("Failed to update status", "error");
    },
  });

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    request: any,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenUpdateDialog = () => {
    if (selectedRequest) {
      setStatusData({
        status: selectedRequest.status || "pending",
        notes: "",
      });
      setUpdateDialogOpen(true);
      handleCloseMenu();
    }
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialogOpen(false);
    setSelectedRequest(null);
    setStatusData({ status: "pending", notes: "" });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<
      string,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      pending: "warning",
      assigned: "info",
      in_progress: "primary",
      interviewing: "secondary",
      candidates_shortlisted: "info",
      completed: "success",
      cancelled: "error",
      reviewing_applications: "info",
      verify_licenses: "warning",
      screening_applications: "info",
    };
    return colors[status] || "default";
  };

  const getUrgencyColor = (urgency: string) => {
    const colors: Record<
      string,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      low: "success",
      medium: "info",
      high: "warning",
      critical: "error",
    };
    return colors[urgency] || "default";
  };

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "assigned", label: "Assigned" },
    { value: "in_progress", label: "In Progress" },
    { value: "interviewing", label: "Interviewing" },
    { value: "candidates_shortlisted", label: "Candidates Shortlisted" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "reviewing_applications", label: "Reviewing Applications" },
    { value: "verify_licenses", label: "Verify Licenses" },
    { value: "screening_applications", label: "Screening Applications" },
  ];

  const requests = data?.data?.data || [];

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Outsourced Staffing Requests
        </Typography>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by job title or facility..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant={statusFilter === "" ? "contained" : "outlined"}
          onClick={() => setStatusFilter("")}
        >
          All
        </Button>
        <Button
          variant={statusFilter === "pending" ? "contained" : "outlined"}
          onClick={() => setStatusFilter("pending")}
        >
          Pending
        </Button>
        <Button
          variant={statusFilter === "in_progress" ? "contained" : "outlined"}
          onClick={() => setStatusFilter("in_progress")}
        >
          In Progress
        </Button>
        <Button
          variant={statusFilter === "completed" ? "contained" : "outlined"}
          onClick={() => setStatusFilter("completed")}
        >
          Completed
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Facility</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Positions</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Urgency</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Applications</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Requested</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 8 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No outsourced requests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests
                .filter((request: any) =>
                  searchTerm
                    ? request.job_info.job_title
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      request.facility_info.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    : true,
                )
                .map((request: any) => (
                  <TableRow key={request._id} hover>
                    <TableCell>{request.job_info.job_title}</TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {request.facility_info.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {request.facility_info.city},{" "}
                          {request.facility_info.state}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {request.stats.positions_filled}/
                      {request.outsourcing_details.positions_needed}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.outsourcing_details.urgency.toUpperCase()}
                        color={getUrgencyColor(
                          request.outsourcing_details.urgency,
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status.replace(/_/g, " ").toUpperCase()}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={`${request.stats.total_applications_reviewed || 0} Total`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${request.stats.candidates_shortlisted || 0} Shortlisted`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(request.outsourcing_details.requested_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            navigate(`/outsourcing/${request._id}/applications`)
                          }
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, request)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleOpenUpdateDialog}>Update Status</MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedRequest) {
              navigate(`/outsourcing/${selectedRequest._id}/applications`);
            }
            handleCloseMenu();
          }}
        >
          View Details
        </MenuItem>
      </Menu>

      {/* Update Status Dialog */}
      <Dialog
        open={updateDialogOpen}
        onClose={handleCloseUpdateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>Update Outsourcing Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                <strong>Job:</strong> {selectedRequest?.job_info?.job_title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Facility:</strong>{" "}
                {selectedRequest?.facility_info?.name}
              </Typography>
            </Box>
            <TextField
              label="Status *"
              select
              fullWidth
              value={statusData.status}
              onChange={(e) =>
                setStatusData({ ...statusData, status: e.target.value })
              }
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes (Optional)"
              multiline
              rows={4}
              fullWidth
              value={statusData.notes}
              onChange={(e) =>
                setStatusData({ ...statusData, notes: e.target.value })
              }
              placeholder="Add any notes about this status update..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={() => statusUpdateMutation.mutate()}
            loading={statusUpdateMutation.isPending}
          >
            Update Status
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutsourcedJobs;
